/* ═══════════════════════════════════════════════════════════════════════════
   ManDana — a transfer you can watch, pause and cancel
   ---------------------------------------------------------------------------
   One component, two mount points. Inline on the desk or record a file was
   dropped on (1.18), and in the account-wide list in Settings (5.5). Not two
   components that look alike: a person who pauses an upload from a desk and
   then opens Settings must find it paused there, and the only way to be sure
   of that is for it to be the same thing.

   PROGRESS IS CHUNKS DONE OVER CHUNKS TOTAL, and it comes off the durable
   queue. Not a byte counter from a transport progress callback: a relay may
   not offer one, and a bar that behaves differently depending on which store
   is behind it has to be re-tested against every store, for ever. Chunks are a
   thing every store agrees about, because the chunking is ours (1.13).

   THE WORDS ARE A PURE FUNCTION, and that is where the thinking is rather than
   a concession to a test runner. Five states, six refusal words and three
   buttons whose presence is a decision each time — every one of them a
   sentence somebody reads at the worst possible moment, when their file has
   not arrived. mdTransferSay is testable on its own; the component below only
   arranges what it said.

   Two laws it may not break:

     MANDANA NEVER SHAMES. Not "you ran out of room". The store refused; say
     what happened, say what is still true, and never make somebody feel they
     did it wrong. tests/no-shame.test.js reads this file.

     A TERMINAL REFUSAL OFFERS CANCEL, NOT RETRY (0.9). Retrying an
     over_allowance is a button that cannot work, pressed by somebody with
     every reason to believe it will. Only `unreachable` is worth retrying —
     and that one is already being retried without being asked, so it does not
     get a button either. What it gets is PAUSE, because the person who needs a
     choice there is the one on a metered connection who wants it to stop.

   NO SHIMMER, NO SPINNER. Reduced motion is a blanket rule in index.html, and
   an indeterminate bar is a promise the queue can always keep honestly: it
   knows exactly how many parts there are.
   ═══════════════════════════════════════════════════════════════════════════ */

/* Every refusal that can reach a person, and what it means for their file.

   Each sentence ends with what is still TRUE — the bytes are here — because
   that is the fact that decides whether somebody panics, and it is the fact a
   status code never carries. `unreachable` is absent on purpose: it is not a
   failure and is never rendered as one. */
const MD_TRANSFER_WHY = {
  over_allowance: 'There is no room left in your account for this file.',
  too_large: 'This file is larger than a single upload can carry.',
  unauthorised: 'Sign in again to finish sending this file.',
  hash_mismatch: 'This file did not arrive as it was sent.',
  bad_ref: 'This file could not be prepared for sending.',
};

/* Said when the store used a word this build has never seen. md-blobs.ts
   treats an unknown reason as terminal and says why: guessing retryable turns
   one unseen word into a loop against somebody's store, and guessing terminal
   shows them something they can act on. Only one of those is recoverable by a
   person, and this is the sentence that lets them. */
const MD_TRANSFER_UNKNOWN = 'This file could not be sent, and the store did not say why.';

const MD_TRANSFER_HERE = 'It is still on this device.';

/** What a transfer row says, and which buttons it offers.

    Takes one row from mdBlobTransfers (or mdBlobProgress) and answers
    `{state, label, detail, actions, value, max, valueText}`. Pure: no store,
    no clock, no DOM — so the words can be argued about in a test rather than
    discovered on somebody's screen. */
function mdTransferSay(row) {
  const total = Math.max(1, Number(row && row.total) || 1);
  const value = Math.max(0, Math.min(total, Number(row && row.done) || 0));
  const state = String((row && row.state) || 'done');
  const reason = String((row && row.reason) || '');
  /* A file small enough not to be chunked has no parts to count. "Uploading 0
     of 1" is a true sentence nobody wants — it is the app talking about its
     own machinery at the one moment somebody is worried about their file. */
  const parts = total > 1 ? ` ${value} of ${total}` : '';
  const said = (state, label, detail, actions) =>
    ({ state, label, detail, actions, value, max: total, valueText: label });

  if (state === 'done' || !row) return said('done', 'Uploaded', '', []);

  if (state === 'paused') {
    return said('paused', total > 1 ? `Paused at${parts}` : 'Paused',
      'Nothing more is being sent until you say so.', ['resume', 'cancel']);
  }

  if (state === 'failed') {
    return said('failed', 'Not uploaded',
      `${MD_TRANSFER_WHY[reason] || MD_TRANSFER_UNKNOWN} ${MD_TRANSFER_HERE}`,
      /* Cancel, and nothing else. See the header. */
      ['cancel']);
  }

  if (reason === 'unreachable') {
    /* Not a failure and never drawn as one. Somebody on a train has lost
       nothing, and telling them they have is the app inventing a problem. */
    return said('waiting', 'Waiting for a connection',
      total > 1
        ? `${value} of ${total} parts have gone up. It carries on by itself.`
        : 'It goes up by itself the moment there is a connection.',
      ['pause', 'cancel']);
  }

  if (value === 0) {
    return said('queued', 'Waiting to upload', '', ['pause', 'cancel']);
  }

  return said('sending', `Uploading${parts}`, '', ['pause', 'cancel']);
}

/* The three words a button can be. Words rather than glyphs, deliberately: the
   icon set has no pause and no resume, and inventing two glyphs to say
   something two words already say plainly is a worse trade than it looks —
   a paused upload is exactly the moment somebody should not be reading
   pictograms. */
const MD_TRANSFER_DO = { pause: 'Pause', resume: 'Resume', cancel: 'Cancel' };

/** One transfer, drawn.

    `row` is what mdBlobTransfers hands back. `onAct(what, hash)` is called with
    'pause' | 'resume' | 'cancel'; the caller owns the door, so this component
    never reaches for md-blobs.ts and can be rendered against a fixture. */
function MdTransfer({ row, onAct, name, style, ...rest }) {
  /* Inside render, never hoisted — a colour taken at module scope freezes the
     theme the first tab to load it happened to be in. */
  const ink = window.ink;
  const light = window.mdIsLight();
  const said = mdTransferSay(row);
  const hash = (row && row.hash) || '';
  const failed = said.state === 'failed';
  const filled = said.max ? said.value / said.max : 0;

  return <div style={{ display: 'grid', gap: 6, padding: '10px 12px',
    borderRadius: 12, background: ink(0.035), ...style }} {...rest}>

    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
      {name ? <span style={{ fontSize: 12.5, fontWeight: 600, color: ink(0.82),
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span> : null}
      <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.7,
        textTransform: 'uppercase', marginInlineStart: name ? 'auto' : 0,
        fontVariantNumeric: 'tabular-nums',
        color: failed ? ink(0.82) : ink(0.5) }}>{said.label}</span>
    </div>

    {/* A real bar. role, min, max and now — and a sentence beside the number,
        because "4" read aloud on its own is not an answer to "where is my
        file?". It is left in place when the upload fails: a bar at 4 of 10 is
        the truth of what happened, and emptying it would say the parts that
        did land were lost. */}
    <div role="progressbar" aria-valuemin={0} aria-valuemax={said.max}
      aria-valuenow={said.value} aria-valuetext={said.valueText}
      style={{ height: 4, borderRadius: 2, background: ink(0.08), overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.round(filled * 100)}%`,
        borderRadius: 2,
        /* The account's own accent where one is set, and the app's purple
           where none is. Two fallbacks rather than one because #C4B5FF is
           lavender chosen to read on the dark ground and is nearly invisible
           on the light one — a bar somebody cannot see is a bar that is not
           there. A failed one goes to ink: the accent is for things that are
           still happening. */
        background: failed ? ink(0.28)
          : `var(--md-accent, ${light ? '#7C68E6' : '#C4B5FF'})`,
        transition: 'width .24s ease' }} />
    </div>

    {said.detail
      ? <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.45, color: ink(0.55) }}>{said.detail}</p>
      : null}

    {said.actions.length ? <div style={{ display: 'flex', gap: 6 }}>
      {said.actions.map((what) => <button key={what} type="button"
        onClick={() => onAct && onAct(what, hash)}
        style={{ appearance: 'none', border: `1px solid ${ink(0.14)}`,
          background: 'transparent', borderRadius: 999, cursor: 'pointer',
          padding: '4px 10px', fontSize: 10.5, fontWeight: 700,
          letterSpacing: 0.7, textTransform: 'uppercase',
          color: what === 'cancel' ? ink(0.5) : ink(0.78) }}>
        {MD_TRANSFER_DO[what]}
      </button>)}
    </div> : null}
  </div>;
}

/** Everything in flight, in one list — 5.5's mount point and 1.18's, filtered.

    Empty is the ORDINARY state and renders as a plain sentence rather than a
    card, because a card that says "nothing" is a thing to look at where there
    should be nothing to look at. */
function MdTransfers({ rows, onAct, empty, nameOf, style }) {
  const ink = window.ink;
  const list = Array.isArray(rows) ? rows : [];
  if (!list.length) {
    return <p style={{ margin: 0, fontSize: 12, color: ink(0.45), ...style }}>
      {empty || 'Nothing is uploading.'}
    </p>;
  }
  return <div style={{ display: 'grid', gap: 8, ...style }}>
    {list.map((row) => <MdTransfer key={row.hash} row={row} onAct={onAct}
      name={nameOf ? nameOf(row) : undefined} />)}
  </div>;
}

Object.assign(window, {
  MD_TRANSFER_WHY, mdTransferSay, MdTransfer, MdTransfers,
});
